/* ============================================
   Markup del partner — de nuestro costo a su precio

   Una cotización de marca blanca tiene DOS precios por ítem y los dos viven
   en `items`: `costo_partner` es lo que el partner nos paga a nosotros, y
   `precio_unitario` es lo que él le cobra a su cliente. Hasta acá el segundo
   se escribía a mano, ítem por ítem, y sólo si la cotización la había creado
   él. Cuando el documento lo armamos nosotros —Nordic, Grupo Central— el
   partner lo abría, veía nuestros precios y no tenía dónde poner los suyos.

   Este módulo es ese «dónde»: un multiplicador que se aplica de una vez a
   todos los ítems que salieron de nuestro catálogo.

   DECISIONES QUE NO SE LEEN DEL CÓDIGO
   ────────────────────────────────────
   · **El markup se guarda, no se consume.** Queda en `cotizaciones.markup` y
     el costo base sigue intacto en `costo_partner`, así que se puede subir,
     bajar o anular cuantas veces haga falta — que es justo lo que pide una
     renegociación con descuento. Si en cambio se escribiera encima de
     `precio_unitario` y se olvidara, bajarlo un 5% al mes siguiente partiría
     del precio ya marcado y no del costo: el descuento saldría mal y nadie
     notaría por qué.

   · **Guarda el `factor` ya resuelto**, no sólo el tipo y el valor. Los
     precios escritos a mano dentro del HTML de una sección (`{{precio:390}}`)
     los expande el backend al renderizar el PDF, y ahí no hay ítems con los
     que recalcular una base. Con el factor adentro, esa expansión es una
     multiplicación y no una segunda implementación de esta aritmética.

   · **Sólo toca los ítems con `costo_partner`.** Los demás son servicios del
     propio partner, ya puestos al precio que él quiere: volver a marcarlos
     sería cobrar dos veces sobre lo mismo.

   · **Un ítem con `precio_fijado` queda afuera.** Es el ítem que él retocó a
     mano después de aplicar el markup. Sin esa marca, el siguiente ajuste
     global le borraría el precio negociado sin avisar.

   · **Monedas distintas se excluyen.** Si el costo está en dólares y la venta
     en soles —el caso de Nordic— no hay factor que convierta una en otra, y
     multiplicar igual daría un número inventado. Esos ítems se listan aparte
     para que él los ponga a mano.
   ============================================ */
import { r2 } from './utils'
import type { CotizacionItemsEstandar, Markup, MarkupTipo } from './types'

export type { Markup, MarkupTipo }

export interface CuotaCronograma {
  numero?: number
  descripcion?: string
  porcentaje?: number
  monto?: number
  condicion?: string
}

/** El ítem salió de nuestro catálogo: hay un costo que el partner nos paga. */
export function esNuestro(it: CotizacionItemsEstandar): boolean {
  return Number(it?.costo_partner) > 0
}

/**
 * ¿El markup puede tocar este ítem? Tres condiciones, cada una por su razón
 * (ver la cabecera): que sea nuestro, que no lo haya fijado a mano, y que el
 * costo esté en la misma moneda en la que se vende.
 */
export function esMarcable(it: CotizacionItemsEstandar, moneda: string): boolean {
  if (!esNuestro(it)) return false
  if (it.precio_fijado) return false
  return (it.costo_partner_moneda || moneda) === moneda
}

/** Los ítems nuestros que están en otra moneda: el markup no los alcanza. */
export function itemsEnOtraMoneda(
  items: CotizacionItemsEstandar[],
  moneda: string,
): CotizacionItemsEstandar[] {
  return items.filter(it => esNuestro(it) && (it.costo_partner_moneda || moneda) !== moneda)
}

/** Los ítems que él fijó a mano y el markup ya no toca. */
export function itemsFijados(items: CotizacionItemsEstandar[]): CotizacionItemsEstandar[] {
  return items.filter(it => esNuestro(it) && it.precio_fijado === true)
}

/**
 * Lo que la cotización nos cuesta a él: la base sobre la que se calcula el
 * markup. Suma sólo lo marcable, porque es sobre eso que el factor opera.
 */
export function costoBase(items: CotizacionItemsEstandar[], moneda: string): number {
  return r2(
    items
      .filter(it => esMarcable(it, moneda))
      .reduce((acc, it) => acc + Number(it.costo_partner || 0) * (Number(it.cantidad) || 0), 0),
  )
}

/**
 * El multiplicador que lleva del costo al precio.
 *
 * Con base 0 devuelve 1 en vez de dividir: una cotización sin ítems nuestros
 * no tiene markup posible, y un `Infinity` acá se propagaría hasta el PDF.
 */
export function factorDeMarkup(tipo: MarkupTipo, valor: number, base: number): number {
  const v = Number(valor)
  if (!Number.isFinite(v)) return 1
  if (tipo === 'porcentaje') return 1 + v / 100
  if (base <= 0) return 1
  if (tipo === 'monto') return (base + v) / base
  return v / base // 'final'
}

/** El factor que corresponde a un markup sobre estos ítems, ya resuelto. */
export function construirMarkup(
  tipo: MarkupTipo,
  valor: number,
  items: CotizacionItemsEstandar[],
  moneda: string,
): Markup {
  return {
    tipo,
    valor: Number(valor) || 0,
    factor: factorDeMarkup(tipo, Number(valor) || 0, costoBase(items, moneda)),
    aplicado_at: new Date().toISOString(),
  }
}

/**
 * Aplica el markup: devuelve los ítems con `precio_unitario` recalculado
 * desde `costo_partner`. **Siempre parte del costo**, nunca del precio
 * anterior, que es lo que hace que aplicar dos veces no acumule.
 *
 * Los ítems que el markup no alcanza vuelven tal cual.
 */
export function aplicarMarkup(
  items: CotizacionItemsEstandar[],
  markup: Markup | null,
  moneda: string,
): CotizacionItemsEstandar[] {
  if (!markup) return items
  return items.map(it => {
    if (!esMarcable(it, moneda)) return it
    const precio = r2(Number(it.costo_partner || 0) * markup.factor)
    return { ...it, precio_unitario: precio, total: r2(precio * (Number(it.cantidad) || 0)) }
  })
}

/**
 * Reparte un subtotal nuevo entre las cuotas del cronograma.
 *
 * Los hitos se guardan con monto Y porcentaje, y el monto es el que se
 * imprime: si el precio cambia y los montos no, el documento cierra mal —el
 * pie del cronograma suma lo impreso, no el total. Se recalcula desde el
 * porcentaje, y **el residuo del redondeo lo absorbe la cuota mayor**, la
 * misma convención que usan los hitos de cobro y la factura de Redivia.
 *
 * Una cuota sin porcentaje conserva su peso relativo sobre el subtotal viejo.
 */
export function escalarCronograma(
  cronograma: CuotaCronograma[] | null | undefined,
  subtotalAntes: number,
  subtotalDespues: number,
): CuotaCronograma[] | null {
  if (!Array.isArray(cronograma) || cronograma.length === 0) return cronograma ?? null
  if (!(subtotalDespues > 0)) return cronograma

  const pesos = cronograma.map(q => {
    const pct = Number(q?.porcentaje)
    if (Number.isFinite(pct) && pct > 0) return pct / 100
    const monto = Number(q?.monto) || 0
    return subtotalAntes > 0 ? monto / subtotalAntes : 1 / cronograma.length
  })

  const montos = pesos.map(p => r2(subtotalDespues * p))
  // El residuo va a la cuota más grande: en la más chica se nota más.
  const suma = r2(montos.reduce((a, b) => a + b, 0))
  const resto = r2(subtotalDespues - suma)
  if (resto !== 0) {
    let mayor = 0
    for (let i = 1; i < montos.length; i++) if (montos[i] > montos[mayor]) mayor = i
    montos[mayor] = r2(montos[mayor] + resto)
  }

  return cronograma.map((q, i) => ({ ...q, monto: montos[i] }))
}

/**
 * ¿El documento todavía lleva NUESTROS precios?
 *
 * No alcanza con «tiene ítems nuestros y no tiene markup»: así estaba escrito
 * al principio y marcó como base la 260031 de Nordic —aceptada, ya en manos
 * del cliente—, que se armó con los precios de venta puestos a mano antes de
 * que el markup existiera. El cliente abría su propuesta y leía una
 * advertencia interna.
 *
 * La condición verdadera es que **nadie le haya puesto precio encima**: todos
 * los ítems nuestros siguen valiendo exactamente lo que el partner nos paga.
 * Con un markup guardado, nunca es base.
 *
 * Un partner que venda a precio de costo cae acá por error, y se prefiere ese
 * error: le muestra un aviso de más a él, mientras que el de antes le mostraba
 * un aviso interno a su cliente.
 */
export function llevaPreciosBase(
  cot: { markup?: unknown; items?: unknown } | null | undefined,
): boolean {
  if (!cot || cot.markup) return false
  if (!Array.isArray(cot.items)) return false
  const nuestros = (cot.items as CotizacionItemsEstandar[]).filter(esNuestro)
  if (nuestros.length === 0) return false
  return nuestros.every(it => Number(it.precio_unitario) === Number(it.costo_partner))
}

/** Lo que el partner gana con este markup, para mostrárselo antes de guardar. */
export function resumenMarkup(
  items: CotizacionItemsEstandar[],
  markup: Markup | null,
  moneda: string,
): { costo: number; venta: number; margen: number; pct: number } {
  const costo = costoBase(items, moneda)
  const conMarkup = aplicarMarkup(items, markup, moneda)
  const venta = r2(
    conMarkup
      .filter(it => esMarcable(it, moneda))
      .reduce((acc, it) => acc + Number(it.precio_unitario || 0) * (Number(it.cantidad) || 0), 0),
  )
  const margen = r2(venta - costo)
  return { costo, venta, margen, pct: costo > 0 ? r2((margen / costo) * 100) : 0 }
}
